import { type JanusCtx, type Row, activeStatuses, ecId, ecLean, handBack, insertRoll, isInventoryRow, itemField, record, requiredInt, upper } from './context.js';
import { isSelfStatusOrigin } from '../statuses/status-origin.js';
import type { JanusInput } from './janus.js';
import { checkOpposingTeam } from './check-opposing-team.js';
import { plCatalogs, statusQuantityInfo } from '../pl/contract.js';
import { empty } from '../php-compat/index.js';
import { getUndispellableStatuses } from '../port/k3-rules/StatusEffect.js';
import { statusItemId } from '../statuses/expiry.js';
import { removeStoredStatus, storedStatusHasSpecialTeardown } from './statuses/status-effect-removal-helpers.js';
import { selectSkillForContestingRoll } from './select-skill-for-contesting-roll.js';
import { skillRollArr } from './skill-roll.js';
import { MAGIC_SKILL } from './charged-cast.js';
import { counterspellCheck } from './counterspell.js';
import { settleSpellResources, spendSpellAction } from './templates/template-spell-roll.js';
import { spendAbilityAction } from './templates/template-abilities-roll.js';
import { janusPostRollChangeAbilityResources } from './charges/adjust-charges.js';

const BENDING = 'F964C0DA-54EA-1EB5-ED00-8E9AD50FFA53';
const UNDISPELLABLE = new Set(getUndispellableStatuses());

/** Pure dispels have their own dispatch; damage/roll hybrids retain a specific pre-draw refusal. */
export function isDispel(ctx: JanusCtx, ability: Row): boolean {
  if (empty(ctx.read.value(ability, 'dispelling_effect')) && empty(ctx.read.value(ability, 'mass_dispelling_effect'))) return false;
  if (['roll_type', 'damage_type', 'dice', 'die'].some(key => ctx.read.has(ability, key)))
    handBack('janus:dispel-hybrid-roll-type');
  return true;
}

/** get_dispellable_effects: only spell-origin catalog boons on enemies/objects, or banes on friends. */
function eligibleEffects(ctx: JanusCtx, caster: Row, target: Row): Row[] {
  const kind = isInventoryRow(target) || checkOpposingTeam(ctx, caster, target) ? 'boon' : 'bane';
  const catalog = plCatalogs().statusEffects;
  return activeStatuses(ctx, target).filter(status => {
    if (isSelfStatusOrigin(ctx.read.value(status, 'status_id'), ctx.read.value(status, 'status_origin_id'))) return false;
    if (ctx.read.value(status, 'status_origin_id_item_type') !== 'spell') return false;
    const id = statusItemId(status), meta = catalog.getById(id);
    if (meta?.status_type !== kind) return false;
    const variable = meta.variable_name;
    const base = statusQuantityInfo(id)?.base ?? variable.replace(/_\d+$/, '');
    return !UNDISPELLABLE.has(variable) && !UNDISPELLABLE.has(base);
  });
}

/** A held full origin defines cantrip level 0 even when lean data omitted spell_level; an identity shell does not. */
function effectLevel(ctx: JanusCtx, status: Row): number {
  if (isSelfStatusOrigin(ctx.read.value(status, 'status_id'), ctx.read.value(status, 'status_origin_id'))) return 0;
  const snapshot = record(ctx.read.entries(status, 'status_origin_arr')[0]);
  const origin = snapshot && record(snapshot.item_layer_types) ? snapshot
    : ctx.read.definition(upper(ctx.read.value(status, 'status_origin_id')));
  if (!origin || !record(origin.item_layer_types)) handBack('janus:dispel-origin-not-held');
  const raw = ctx.read.value(origin, 'spell_level');
  return raw == null ? 0 : requiredInt(raw, 'janus:dispel-origin-not-held');
}

/** Prove EVERY eligible row before a random pick, counterspell, orb, or contest can draw. */
export function dispelPreDraw(ctx: JanusCtx, caster: Row, targets: Row[]): void {
  for (const target of targets) for (const status of eligibleEffects(ctx, caster, target)) {
    if (storedStatusHasSpecialTeardown(ctx, status)) handBack('janus:dispel-special-teardown');
    effectLevel(ctx, status);
  }
}

function originalCaster(ctx: JanusCtx, caster: Row, status: Row): Row | null {
  const original = ctx.members.get(upper(ctx.read.value(status, 'status_caster_id')));
  const encounter = upper(ecLean(caster, 'encounter_id')) || upper(ctx.read.value(ctx.encounter, 'encounter_id'));
  const originalEncounter = original ? upper(ecLean(original, 'encounter_id') ?? ctx.read.value(ctx.encounter, 'encounter_id')) : '';
  return original && ecId(original) && encounter && originalEncounter === encounter ? original : null;
}

/** resolve_dispel_contest: defender first with UNCLAMPED effect-minus-cast level; absent defender uses DC 10 + that difference. */
function contest(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, status: Row): boolean {
  const authored = requiredInt(ctx.read.value(ability, 'spell_level') ?? 0, 'template_spell_roll:invalid-level');
  const level = authored > 0 ? Math.max(authored, ctx.castLevel ?? authored) : authored;
  const difference = effectLevel(ctx, status) - level;
  const skillId = ctx.itemCast ? MAGIC_SKILL : BENDING;
  const original = originalCaster(ctx, caster, status);
  let dc = 10 + difference;
  if (original) {
    const skill = selectSkillForContestingRoll(ctx, original, [skillId]);
    skill.modifier += difference;
    dc = skillRollArr(ctx, input, original, skill, null, 0, { asTarget: true, isTarget: true });
  }
  return skillRollArr(ctx, input, caster, selectSkillForContestingRoll(ctx, caster, [skillId]), null, original ? 0 : dc) >= dc;
}

/** janus_dispel: one random effect per target, or every effect in order; removals use the shared single-connection/loss-trigger tail. */
export function dispelCast(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  dispelPreDraw(ctx, caster, targets);
  const spell = ['spell', 'curse'].includes(String(itemField(ability, 'item_type')));
  if (spell && counterspellCheck(ctx, input, ability, caster)) { spendSpellAction(ctx, ability, caster, true); return; }
  if (!spell) spendAbilityAction(ctx, ability, caster);
  const mass = !empty(ctx.read.value(ability, 'mass_dispelling_effect'));
  const name = String(ecLean(caster, 'item_name') ?? itemField(caster, 'item_name') ?? 'Caster');
  for (const target of targets) {
    const eligible = eligibleEffects(ctx, caster, target);
    const targetName = String(ecLean(target, 'item_name') ?? itemField(target, 'item_name') ?? 'Target');
    let kind = 'dispel_no_eligible';
    let removed = 0;
    if (eligible.length) {
      const picks = mass ? eligible : [eligible.length === 1 ? eligible[0]! : eligible[ctx.roll.d(eligible.length) - 1]!];
      for (const status of picks) {
        if (!contest(ctx, input, ability, caster, status)) continue;
        // PHP contests the original list even when a prior single-connection tail retired this peer.
        // Re-read by identity: production status readers return shallow copies, never stable row objects.
        const held = activeStatuses(ctx, target).find(row => upper(ctx.read.value(row, 'status_id')) === upper(ctx.read.value(status, 'status_id')));
        if (held) removeStoredStatus(ctx, target, held, 'single');
        removed++;
      }
      kind = mass ? (removed ? 'mass_dispel_success' : 'mass_dispel_failed') : (removed ? 'dispel_success' : 'dispel_failed');
    }
    insertRoll(ctx, { roll_type_1: kind, roll_item_id: input.abilityId, by: caster, to: target, logMessage: true });
    (ctx.warnings ??= []).push(!eligible.length ? `${name} finds no eligible spell effects to dispel on ${targetName}.`
      : mass ? (removed ? `${name} dispels ${removed} spell effect(s) on ${targetName}.` : `${name}'s mass dispel removes no effects on ${targetName}.`)
        : removed ? `${name} successfully dispels an effect on ${targetName}.` : `${name}'s dispel fails against an effect on ${targetName}.`);
  }
  // D3 worker judgment: authored spell/ability costs replace the legacy free-dispel short circuit.
  // Spell settlement owns the post-roll hook, including the charged source's exactly-once billing.
  if (spell) { settleSpellResources(ctx, input, ability, caster); spendSpellAction(ctx, ability, caster); }
  else janusPostRollChangeAbilityResources(ctx, ability, caster, input.useAbilityVariable1, undefined, input);
}
